import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AllOptions from './AllOptions';
import Search from './Search';
import ToggleButtons from './ToggleButtons';
import ModeToggle from './ModeToggle';

/**
 * Left pane of the dynamic-hint filter — header label + search row +
 * scrollable list. Logic preserved 1:1 from the legacy version: the
 * `searchValue` object keeps independent search terms for the
 * "pre-filter" and "all" alignments, the `alignment` toggle wipes
 * itself when the popover closes. Visual changes only — the inner
 * widgets are now token-driven (Input + segmented control + DS
 * checkboxes) so this pane reads as part of the QHS design system.
 */
const SearchSection = ({
  draft,
  mode,
  onModeChange,
  onToggleOption,
  dataKey,
  handleClose,
  open,
  tableName,
  fields,
  tableFilters,
  includeCurrentAccount,
  customAccountUuidForFilter,
  staticFilters,
  disableExcludeMode = false,
}) => {
  const { tables } = useSelector(state => state.i18n);
  const [searchValue, setSearchValue] = useState({
    left: '',
    right: '',
  });
  const [alignment, setAlignment] = useState('left');

  const handleChange = newAlignment => {
    if (newAlignment) setAlignment(newAlignment);
  };

  const updateSearchValue = value => {
    setSearchValue({
      ...searchValue,
      [alignment]: value,
    });
  };

  useEffect(() => {
    if (!open) {
      setSearchValue({ left: '', right: '' });
      setAlignment('left');
    }
  }, [open]);

  return (
    <div className="qhs-dynh-pane">
      {/*
        The mode switch sits on the header line rather than in the
        toolbar below, for two reasons. The toolbar already carries a
        segmented control (pre-filter / all); a second one directly
        under it would read as a pair of related settings, and they are
        unrelated. And the header line is otherwise empty, so this costs
        no vertical space — which the popover has none of to spare.

        Some columns cannot be excluded at all: the API answers 422 to a
        negated term on a per-document bot-activity field, for instance.
        Those pass `disableExcludeMode` and the switch is absent rather
        than present-and-broken.
      */}
      <div className="qhs-dynh-section-head">
        <span className="qhs-dynh-section-head__label">
          {tables?.availableOptions || 'Available options'}
        </span>
        {!disableExcludeMode && <ModeToggle mode={mode} onChange={onModeChange} />}
      </div>
      <div className="qhs-dynh-toolbar">
        <Search
          searchValue={alignment === 'left' ? searchValue.left : searchValue.right}
          setSearchValue={updateSearchValue}
        />
        <ToggleButtons alignment={alignment} handleChange={handleChange} />
      </div>
      <AllOptions
        draft={draft}
        mode={mode}
        onToggleOption={onToggleOption}
        dataKey={dataKey}
        handleClose={handleClose}
        open={open}
        tableName={tableName}
        searchValue={searchValue.left}
        searchValueAll={searchValue.right}
        fields={fields}
        tableFilters={tableFilters}
        includeCurrentAccount={includeCurrentAccount}
        showOnlyAvailableOptions={alignment === 'left'}
        customAccountUuidForFilter={customAccountUuidForFilter}
        staticFilters={staticFilters}
      />
    </div>
  );
};

export default SearchSection;
